import { Zap, BarChart3, FileText, ArrowRight, Upload, Sparkles, Tag, Image } from "lucide-react";

const AISoftwareSection = () => {
  return (
    <section className="py-16 md:py-24 px-4 bg-black relative overflow-hidden" data-testid="ai-software-section">
      {/* Red glow accents */}
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-red-600/8 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-blue-600/6 rounded-full blur-[120px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 text-white rounded-lg text-xs font-black tracking-widest mb-6">
            <Zap size={14} />
            INDIA'S FIRST AI CATALOG SOFTWARE
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
            Generate Marketplace Catalog<br />
            <span className="text-red-500">in 90 Seconds</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            AI-powered auto listings & data analysis software — available on rent for sellers
          </p>
        </div>

        {/* Main Content - Bento Grid */}
        <div className="grid lg:grid-cols-3 gap-4 max-w-6xl mx-auto mb-12">
          
          {/* Left - Video Demo */}
          <div className="lg:col-span-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-red-500/30 transition-all duration-500">
            <div className="relative pb-[56.25%] bg-gray-900">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/g8iFk0UvSls?rel=0&modestbranding=1"
                title="BrandBorn AI Catalog Generator Demo - ecomcatalog.ai"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <div className="p-5 flex items-center justify-between">
              <div>
                <p className="text-white font-bold text-sm">Watch: AI Catalog in Action</p>
                <p className="text-gray-500 text-xs">See how brands create listings in 90 seconds</p>
              </div>
              <a
                href="https://ecomcatalog.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 bg-red-600 text-white rounded-lg font-bold text-sm hover:bg-red-700 transition-all shadow-lg shadow-red-600/25"
              >
                Try It Free
              </a>
            </div>
          </div>

          {/* Right - Feature Stack */}
          <div className="flex flex-col gap-4">
            {/* Drag & Drop */}
            <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:border-blue-500/30 transition-all group">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Upload size={20} className="text-white" />
              </div>
              <h4 className="text-white font-bold text-sm mb-1">Drag & Drop Images</h4>
              <p className="text-gray-500 text-xs">Upload product photos, AI does the rest</p>
            </div>

            {/* AI Titles */}
            <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:border-red-500/30 transition-all group">
              <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Sparkles size={20} className="text-white" />
              </div>
              <h4 className="text-white font-bold text-sm mb-1">AI-Generated Titles</h4>
              <p className="text-gray-500 text-xs">SEO-optimized for each marketplace</p>
            </div>

            {/* AI Descriptions */}
            <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:border-blue-500/30 transition-all group">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <FileText size={20} className="text-white" />
              </div>
              <h4 className="text-white font-bold text-sm mb-1">AI Descriptions</h4>
              <p className="text-gray-500 text-xs">Compelling copy that converts buyers</p>
            </div>

            {/* SEO Tags */}
            <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:border-red-500/30 transition-all group">
              <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Tag size={20} className="text-white" />
              </div>
              <h4 className="text-white font-bold text-sm mb-1">SEO Tags & Keywords</h4>
              <p className="text-gray-500 text-xs">Auto-generated for maximum visibility</p>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-10">
          {[
            { val: "90s", label: "Per Listing", color: "text-red-500" },
            { val: "10+", label: "Platforms", color: "text-blue-400" },
            { val: "98%", label: "Approval Rate", color: "text-red-500" },
            { val: "Rent", label: "Monthly Plans", color: "text-blue-400" },
          ].map((s, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center hover:bg-white/10 transition-all">
              <div className={`text-3xl font-black ${s.color} mb-1`}>{s.val}</div>
              <div className="text-gray-500 text-xs font-bold uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <a
            href="https://ecomcatalog.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-12 py-5 bg-red-600 text-white rounded-2xl font-black text-lg tracking-wider uppercase hover:bg-red-700 transition-all duration-500 shadow-[0_4px_20px_rgba(220,38,38,0.3)] hover:shadow-[0_12px_40px_rgba(220,38,38,0.4)] hover:-translate-y-1"
            data-testid="ai-software-cta"
          >
            Generate AI Catalog Now
            <ArrowRight size={22} />
          </a>
          <p className="text-gray-600 text-sm mt-4">No credit card required. Start free at ecomcatalog.ai</p>
        </div>
      </div>
    </section>
  );
};

export default AISoftwareSection;
